/**
 * Erkennt Ergebnisse von Werkzeugen, die selbst keine Spur hinterlassen:
 * AutoAusfüllen einer Reihe und die Blitzvorschau. Beide erzeugen feste Werte;
 * geprüft wird deshalb, ob die Werte das typische Muster haben.
 */

import type { CellInfo, SheetInfo } from "@/office/excel/workbook";
import { hasFormula, isNumber, isText } from "@/office/excel/workbook";
import { istDatumsformat } from "@/office/excel/zahlenformat";

function spalten(sheet: SheetInfo): Map<number, CellInfo[]> {
  const ergebnis = new Map<number, CellInfo[]>();
  for (const zelle of sheet.cells) {
    const liste = ergebnis.get(zelle.column) ?? [];
    liste.push(zelle);
    ergebnis.set(zelle.column, liste);
  }
  for (const liste of ergebnis.values()) liste.sort((a, b) => a.row - b.row);
  return ergebnis;
}

function alsDatum(seriennummer: number): Date {
  return new Date(Math.round((seriennummer - 25569) * 86400000));
}

function istMonatsfolge(werte: number[]): boolean {
  const daten = werte.map(alsDatum);
  return daten.every((datum, i) => {
    if (i === 0) return true;
    const vorher = daten[i - 1];
    const monate = (datum.getUTCFullYear() - vorher.getUTCFullYear()) * 12 + datum.getUTCMonth() - vorher.getUTCMonth();
    return monate === 1;
  });
}

export interface Reihe {
  sheetName: string;
  start: string;
  laenge: number;
  art: "Zahlenreihe" | "Datumsreihe" | "Monatsreihe";
}

/**
 * Sucht in jeder Spalte eine Folge von mindestens `mindestens` festen Zahlen
 * mit gleichem Abstand – oder Datumswerte im Monatsabstand.
 */
export function findeReihen(sheets: SheetInfo[], mindestens = 5): Reihe[] {
  const reihen: Reihe[] = [];
  for (const sheet of sheets) {
    for (const liste of spalten(sheet).values()) {
      let lauf: CellInfo[] = [];
      const pruefeLauf = () => {
        if (lauf.length >= mindestens) {
          const werte = lauf.map((zelle) => Number(zelle.value));
          const schritt = werte[1] - werte[0];
          const gleichmaessig = schritt !== 0 && werte.every((wert, i) => i === 0 || Math.abs(wert - werte[i - 1] - schritt) < 1e-9);
          const datum = istDatumsformat(lauf[0]);
          if (gleichmaessig) {
            reihen.push({ sheetName: sheet.name, start: lauf[0].address, laenge: lauf.length, art: datum ? "Datumsreihe" : "Zahlenreihe" });
          } else if (datum && istMonatsfolge(werte)) {
            reihen.push({ sheetName: sheet.name, start: lauf[0].address, laenge: lauf.length, art: "Monatsreihe" });
          }
        }
        lauf = [];
      };
      for (const zelle of liste) {
        const passt = isNumber(zelle) && !hasFormula(zelle) && (lauf.length === 0 || zelle.row === lauf[lauf.length - 1].row + 1);
        if (passt) lauf.push(zelle);
        else {
          pruefeLauf();
          if (isNumber(zelle) && !hasFormula(zelle)) lauf.push(zelle);
        }
      }
      pruefeLauf();
    }
  }
  return reihen;
}

export interface Ableitung {
  sheetName: string;
  quelle: string;
  ziel: string;
  zeilen: number;
}

/**
 * Sucht eine Spalte mit festen Texten, die in jeder Zeile ein ganzes Wort aus
 * einer anderen Spalte derselben Zeile sind – das Muster der Blitzvorschau
 * (Vorname aus „Anna Weber“, Ort aus „10115 Berlin“).
 */
export function findeAbleitungen(sheets: SheetInfo[], mindestens = 3): Ableitung[] {
  const ergebnis: Ableitung[] = [];
  for (const sheet of sheets) {
    const nachSpalte = spalten(sheet);
    for (const [quellSpalte, quelle] of nachSpalte) {
      for (const [zielSpalte, ziel] of nachSpalte) {
        if (quellSpalte === zielSpalte) continue;
        let treffer = 0;
        let ersteZeile = "";
        let zielStart = "";
        for (const zelle of ziel) {
          if (!isText(zelle) || hasFormula(zelle)) continue;
          const partner = quelle.find((q) => q.row === zelle.row);
          if (!partner || !isText(partner)) continue;
          const woerter = String(partner.value).trim().split(/[\s,;]+/);
          const teil = String(zelle.value).trim();
          if (woerter.length >= 2 && teil.length > 0 && woerter.includes(teil)) {
            treffer += 1;
            if (!ersteZeile) {
              ersteZeile = partner.address;
              zielStart = zelle.address;
            }
          }
        }
        if (treffer >= mindestens) {
          ergebnis.push({ sheetName: sheet.name, quelle: ersteZeile, ziel: zielStart, zeilen: treffer });
        }
      }
    }
  }
  return ergebnis;
}
